import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import useMotionPreference from "@/hooks/useMotionPreference";
import { Check, Palette, X } from "lucide-react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "portfolio-accent";
const COLORS = [
  { id: "blue", label: "Blue", color: "#3b82f6", ink: "#050505" },
  { id: "pink", label: "Pink", color: "#ec4899", ink: "#050505" },
  { id: "orange", label: "Orange", color: "#f97316", ink: "#050505" },
  { id: "yellow", label: "Yellow", color: "#facc15", ink: "#050505" },
  { id: "purple", label: "Purple", color: "#a855f7", ink: "#050505" },
];
const isColor = (value) => COLORS.some(({ id }) => id === value);

function savedColor() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isColor(saved) ? saved : "blue";
  } catch {
    return "blue";
  }
}

export default function AccentPicker() {
  const [accent, setAccent] = useState(savedColor);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const toggleRef = useRef(null);
  const firstColorRef = useRef(null);
  const reduceMotion = useMotionPreference();
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    document.documentElement.dataset.accent = accent;
    try { localStorage.setItem(STORAGE_KEY, accent); } catch { /* Still works when storage is unavailable. */ }
  }, [accent]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) setAccent(isColor(event.newValue) ? event.newValue : "blue");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!open) return;
    firstColorRef.current?.focus();
    const closeOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (pathname.startsWith("/admin")) return null;
  const selected = COLORS.find(({ id }) => id === accent);

  return (
    <div ref={containerRef} className="accent-picker fixed z-[60]" data-testid="accent-picker">
      <AnimatePresence>
        {open && (
          <motion.div
            id="accent-options"
            inert={!open}
            initial={reduceMotion ? false : { opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: reduceMotion ? 0 : 6, scale: reduceMotion ? 1 : 0.98 }}
            transition={{ duration: reduceMotion ? 0 : 0.18 }}
            className="accent-picker-panel absolute bottom-full left-0 mb-3 origin-bottom-left rounded-2xl border border-white/15 bg-[#111214]/95 p-4 shadow-2xl backdrop-blur-xl"
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <p id="accent-options-label" className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">Accent color</p>
              <span aria-live="polite" className="text-xs text-brand-300">{selected.label}</span>
            </div>
            <div role="group" aria-labelledby="accent-options-label" className="grid grid-cols-5 gap-1">
              {COLORS.map(({ id, label, color, ink }, index) => (
                <button
                  key={id}
                  ref={index === 0 ? firstColorRef : undefined}
                  type="button"
                  aria-label={`${label} accent`}
                  aria-pressed={accent === id}
                  onClick={() => setAccent(id)}
                  className="focus-ring group flex min-h-11 min-w-0 flex-col items-center gap-1.5 rounded-lg py-1.5 text-[10px] text-white/60 transition-colors hover:bg-white/5 hover:text-white"
                >
                  <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 transition-transform group-hover:scale-110 ${accent === id ? "border-white" : "border-transparent"}`} style={{ backgroundColor: color, color: ink }}>
                    {accent === id && <Check aria-hidden="true" className="h-4 w-4" strokeWidth={3} />}
                  </span>
                  {label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        ref={toggleRef}
        type="button"
        aria-label={open ? "Close accent colors" : "Change accent color"}
        aria-expanded={open}
        aria-controls="accent-options"
        onClick={() => setOpen((value) => !value)}
        className="focus-ring flex h-11 min-w-11 items-center justify-center gap-2.5 rounded-full border border-white/15 bg-[#111214]/95 px-3 text-white/80 shadow-lg backdrop-blur-xl transition-colors hover:border-brand-500/50 hover:text-white sm:px-4"
      >
        {open ? <X aria-hidden="true" className="h-4 w-4" /> : <Palette aria-hidden="true" className="h-4 w-4" />}
        <span className="hidden text-xs font-medium sm:inline">Accent</span>
        <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-brand-500" />
      </button>
    </div>
  );
}
